// FNP-1192 — mirrors the FNP-1184 read schema and the FNP-1261 write schema
// (flexi-domains-api/src/appsync-api/src/schema/features/dns-zones.graphql).
//
//   listDnsRecords(connectionId: ID!, zoneId: ID!, filter: DnsRecordsFilterInput,
//                  limit: Int, nextToken: String): DnsRecordsConnection
//   applyDnsRecordChanges(input: ApplyDnsRecordChangesInput!): DnsWriteResult
//
// Four rules travel with these types and are enforced by the code below them:
//
//  1. A record has NO ID. Its identity is (name, type) within a zone — there is
//     no `id` field on `DnsRecord` and nothing here may invent one that gets
//     sent back to the API. See `recordKey` below for the local row key.
//  2. `values` is ALREADY the answer, pointer or not. The adapter resolves a
//     pointer's target into `values` and sets `isPointer`. Nothing here reads a
//     separate target field, because the schema has none.
//  3. `nextToken != null` is the ONLY end-of-results signal. `limit` applies
//     BEFORE the filter, so an EMPTY page with a live token is normal.
//  4. `writeState` has THREE members and NOT_REPORTED is not a synonym for
//     SETTLED. See `writeStateCopy` for the wording rules the schema imposes.

// ⚠️ NO `id` FIELD, DELIBERATELY — the schema has none.
//
// A DNS record is identified by its name and type within a zone. Adding a
// synthetic `id` here would be a field the API never sent and can never accept
// back, and the first person to pass one to `applyDnsRecordChanges` would be
// writing to a record that does not exist.
export type DnsRecord = {
    // Fully-qualified record name, no trailing dot.
    name: string,
    // Record type, uppercase: "A", "CNAME", "TXT".
    type: string,
    // ⚠️ NULLABLE, and not merely because some providers omit it. A record that
    // POINTS AT ANOTHER RESOURCE has no TTL of its own — the provider resolves
    // the pointer and answers with the TARGET's TTL. Rendering 0 here would put
    // a number on screen that no provider ever reported.
    ttlSeconds?: number | null,
    // ⚠️ THE POINTER CASE IS ALREADY RESOLVED INTO THIS FIELD.
    //
    // Where a record points at another resource — a Route 53 alias, a
    // Cloudflare flattened CNAME — the provider returns NO rdata and the target
    // sits in a separate provider-specific structure. The ADAPTER resolves that
    // and renders the target here, so `values` always answers the question
    // "what does this record answer with". A UI that renders this field renders
    // the target for free; a UI that goes looking for a `target` field finds
    // nothing and shows a blank cell, which is the exact bug FNP-1192 exists to
    // avoid.
    values: string[],
    // True when `values` holds a RESOLVED POINTER TARGET rather than literal
    // rdata. Display-only callers can ignore it; anything that EDITS must not,
    // because a pointer is not editable as rdata (see `isEditableAsRdata`).
    isPointer: boolean,
}

export type DnsRecordsConnectionResult = {
    items: DnsRecord[],
    nextToken?: string | null,
}

export type ListDnsRecords = {
    listDnsRecords: DnsRecordsConnectionResult
}

// ⚠️ A LOCAL ROW KEY, NEVER SENT TO THE API.
//
// `MantineDataTable` needs an `idAccessor`, and a record has no id. The pair
// (name, type) IS the record's identity within a zone, so it composes into a
// stable key — stable across refetches, which a array index would not be.
//
// The separator is a character that cannot appear in either half: a DNS name is
// letters, digits, hyphens and dots, and a type is uppercase letters. Using '#'
// means no two distinct records can collide on one key.
export const recordKey = (record: Pick<DnsRecord, 'name' | 'type'>): string =>
    `${record.name}#${record.type}`;

// ⚠️ A POINTER IS NOT EDITABLE AS RDATA, and this is the one gate that says so.
//
// `values` on a pointer holds the RESOLVED TARGET — what the provider answers
// with, not what is stored. Submitting it back as an UPSERT would convert a
// Route 53 alias into a literal A record holding today's resolved address:
// silently correct at the moment of the write, and wrong the moment the target
// moves. The alias would be gone and nothing would report it.
//
// So the edit affordance is withheld for pointers rather than pre-filled with a
// value that is a lie about what the record is.
export const isEditableAsRdata = (record: DnsRecord): boolean => !record.isPointer;

// ⚠️ A POINTER CANNOT BE DELETED THROUGH THIS PATH EITHER (FNP-1279).
//
// Separate from `isEditableAsRdata` despite testing the same field, because
// they are different claims about different actions and they will not always
// agree: if the port ever carries enough of an alias to reconstruct one, a
// delete becomes possible while editing it as rdata stays wrong forever.
// Folding them into one predicate now would have to be unpicked then.
//
// The reason a delete is refused is its own: a provider matches a delete on
// the record's FULL definition, alias target included. The neutral record
// holds only the resolved target name, so the change Flexi can build is not
// the record that exists — the provider either rejects it, or matches a
// literal record of the same name and type and deletes THAT one. The second
// outcome is the dangerous one and it is why this gate is not merely cosmetic.
export const isDeletableRecord = (record: DnsRecord): boolean => !record.isPointer;

// ─── THE WRITE VOCABULARY (FNP-1261) ───

export type DnsRecordChangeAction = 'UPSERT' | 'DELETE';

// ⚠️ NO BARE CREATE, matching the schema. UPSERT is create-or-replace, because
// providers disagree about whether creating an existing record is an error and
// idempotent writes are the only kind this platform wants.
export type DnsRecordChangeInput = {
    action: DnsRecordChangeAction,
    name: string,
    type: string,
    // ⚠️ REQUIRED here though nullable on `DnsRecord`. A record being READ may
    // be a pointer with no TTL of its own; a record being WRITTEN is literal
    // rdata and always carries one.
    ttlSeconds: number,
    values: string[],
}

export type ApplyDnsRecordChangesInput = {
    connectionId: string,
    // Flexi's own opaque zone id, as minted by the adapter. Never a provider id.
    zoneId: string,
    // At least one. An empty batch is refused before any provider is reached.
    changes: DnsRecordChangeInput[],
}

// ⚠️ THREE STATES, AND THE THIRD IS THE POINT.
//
//   PENDING      — accepted by the provider, NOT yet propagated. Not done.
//   SETTLED      — live across the PROVIDER'S OWN nameservers. That is the
//                  CEILING of the claim: resolvers elsewhere keep serving the
//                  old value until the TTL expires. Never worded "live" or
//                  "done" (schema comment, FNP-1261).
//   NOT_REPORTED — this provider does not report propagation AT ALL. NOT a
//                  synonym for SETTLED: a console rendering it as done asserts
//                  on no evidence. Absence of a signal is not a signal.
export type DnsWriteState = 'PENDING' | 'SETTLED' | 'NOT_REPORTED';

export type DnsWriteResult = {
    appliedCount: number,
    // Non-null on the schema: every adapter answers this, so a caller never has
    // to treat "no state" as a fourth case.
    writeState: DnsWriteState,
    // ⚠️ NULLABLE and OPAQUE — never parsed. Not every provider issues one, and
    // the status deliberately does not live inside it.
    providerChangeReference?: string | null,
}

export type ApplyDnsRecordChanges = {
    applyDnsRecordChanges: DnsWriteResult | null
}

// WHAT THE HUMAN IS TOLD ABOUT A WRITE, PER STATE.
//
// ⚠️ THE THREE STATES MUST READ DIFFERENTLY, and two specific constraints come
// straight from the schema comments rather than from taste:
//
//   - SETTLED must NOT be worded "live" or "done". The provider's own
//     nameservers agree; resolvers elsewhere serve the OLD value until the
//     record's TTL expires, and nothing in this system knows when that is.
//     "Live at your provider" is honest. "Live" is not.
//   - NOT_REPORTED must render VISIBLY DIFFERENTLY from SETTLED, and must SAY
//     the provider does not report propagation. Collapsing it into SETTLED
//     claims knowledge nothing observed.
//
// Not a module-scope object literal: I18n.get must run at call time, after the
// language is configured, or every string is captured in the default locale.
export type WriteStateCopy = {
    title: string,
    detail: string,
    // Distinct colours so the three are tellable apart at a glance, not only by
    // reading. NOT_REPORTED is deliberately not green.
    color: string,
}

export const writeStateCopy = (state: DnsWriteState): WriteStateCopy => {

    switch (state) {

        case 'PENDING':
            return {
                title: 'Change accepted, not yet propagated',
                detail: 'The provider has accepted the change but has not finished applying it across its nameservers. It is not live yet.',
                color: 'yellow',
            };

        // ⚠️ "Live at your provider" — NOT "live", NOT "done", NOT "complete".
        // The TTL caveat is part of the message rather than a footnote,
        // because it is the difference between what this state proves and what
        // a reader will otherwise assume it proves.
        case 'SETTLED':
            return {
                title: 'Live on your provider\'s nameservers',
                detail: 'The provider reports the change is applied across its own nameservers. Resolvers elsewhere may keep answering with the previous value until the record\'s TTL expires.',
                color: 'teal',
            };

        // ⚠️ NOT green, and the wording states the ABSENCE of evidence rather
        // than implying success. This provider has no change-tracking API, so
        // nothing here can say whether the change landed.
        case 'NOT_REPORTED':
            return {
                title: 'Applied — this provider does not report propagation',
                detail: 'The change was accepted. This provider has no way to report whether it has propagated, so {product} cannot say whether it has taken effect.',
                color: 'gray',
            };
    }
};

// ─── FILTERING ───

// ⚠️ THE FILTER IS SERVER-SIDE AND DELIBERATELY NARROW.
//
// The schema offers exact `name` and exact `type` only, and the comment is
// emphatic about why: a `contains` on a record name would be filtered by ONE
// provider server-side and by Flexi client-side for another, so identical
// arguments would page differently depending on the provider — the exact leak
// the whole neutral layer exists to prevent.
export type DnsRecordsFilterInput = {
    name?: string,
    type?: string,
}

// ⚠️ NS IS IN THIS LIST FOR A REASON THAT IS NOT "it is another record type".
//
// The nameserver records for a delegated subdomain ARE the delegation. Filtering
// to NS is what makes the delegation chain visible in the interface rather than
// something inferred from a design document, and the ticket names that as the
// thing to demonstrate. It leads the list rather than sitting in alphabetical
// order among the rest.
export const DELEGATION_RECORD_TYPE = 'NS';

// The types offered when the connection reports no `supportedRecordTypes` of
// its own. A conservative, universally-supported set — every provider on the
// catalog handles all of these.
//
// ⚠️ This is a FALLBACK for display, never a claim about a specific provider.
// Where the connection reports its own list, that list wins — see
// useRecordTypeOptions.
export const COMMON_RECORD_TYPES = [
    'A',
    'AAAA',
    'CNAME',
    'MX',
    'NS',
    'TXT',
    'SRV',
    'CAA',
    'PTR',
];
